import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  MagnifyingGlass, BookmarkSimple, ArrowSquareOut, Quotes,
  CaretDown, CaretUp, Funnel, CalendarBlank, Users as UsersIcon,
  BookOpen, ArrowRight, X, Check, FolderSimplePlus, FolderSimple,
  Spinner, ClipboardText, Copy
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { formatAPA, formatMLA, formatChicago, CITATION_STYLES } from '../../utils/citations';

export default function ResearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [yearFilter, setYearFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedPaper, setExpandedPaper] = useState(null);
  const [paperDetail, setPaperDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Bookmark dialog
  const [bookmarkPaper, setBookmarkPaper] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  // Citation dialog
  const [citePaper, setCitePaper] = useState(null);

  // Load bookmarks to show which papers are already saved
  useEffect(() => {
    const loadBookmarks = async () => {
      try {
        const bm = await api.getBookmarks();
        setBookmarkedIds(new Set(bm.map(b => b.paper_id)));
      } catch {}
    };
    loadBookmarks();
  }, []);

  const searchPapers = useCallback(async (newOffset = 0) => {
    if (!query.trim()) return;
    setLoading(true);
    setOffset(newOffset);
    try {
      const data = await api.searchPapers(query.trim(), newOffset, 20, yearFilter || null);
      setResults(data);
    } catch (err) {
      toast.error('Search failed. Please try again.');
    }
    setLoading(false);
  }, [query, yearFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    searchPapers(0);
  };

  const loadPaperDetail = async (paperId) => {
    if (expandedPaper === paperId) { setExpandedPaper(null); return; }
    setExpandedPaper(paperId);
    setDetailLoading(true);
    try {
      const detail = await api.getPaperDetails(paperId);
      setPaperDetail(detail);
    } catch {
      toast.error('Failed to load paper details');
    }
    setDetailLoading(false);
  };

  const openBookmarkDialog = async (paper) => {
    setBookmarkPaper(paper);
    try {
      const f = await api.getFolders(null);
      setFolders(f);
    } catch {}
  };

  const handleBookmark = async () => {
    if (!selectedFolder || !bookmarkPaper) return;
    try {
      await api.bookmarkPaperToFolder(selectedFolder, bookmarkPaper);
      toast.success('Paper bookmarked!');
      setBookmarkedIds(prev => new Set([...prev, bookmarkPaper.paperId]));
      setBookmarkPaper(null);
      setSelectedFolder(null);
    } catch (err) {
      toast.error('Failed to bookmark paper');
    }
  };

  const handleCreateFolderAndBookmark = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      const folder = await api.createFolder(newFolderName.trim());
      setFolders(prev => [...prev, folder]);
      setSelectedFolder(folder.id);
      setNewFolderName('');
      toast.success(`Created folder "${newFolderName.trim()}"`);
    } catch (err) {
      toast.error('Failed to create folder');
    }
    setCreatingFolder(false);
  };

  const papers = results?.papers || [];
  const total = results?.total || 0;

  return (
    <div className="space-y-6" data-testid="research-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Research
        </h1>
        <p className="text-sm text-slate-400 mt-1">Search scientific literature powered by Semantic Scholar</p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2" data-testid="research-search-form">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search papers, topics, or authors..."
            className="pl-10 h-11 text-base"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="research-search-input"
          />
        </div>
        <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white h-11 px-6" disabled={loading} data-testid="research-search-btn">
          {loading ? <Spinner size={18} className="animate-spin" /> : 'Search'}
        </Button>
        <Button type="button" variant="outline" className="h-11" onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle">
          <Funnel size={16} />
        </Button>
      </form>

      {/* Filters */}
      {showFilters && (
        <div className="flex items-center gap-4 p-3 bg-slate-800 rounded-lg border border-slate-700" data-testid="search-filters">
          <div className="flex items-center gap-2">
            <CalendarBlank size={14} className="text-slate-400" />
            <label className="text-xs font-medium text-slate-400">Year:</label>
            <Input
              placeholder="e.g. 2020-2026"
              className="w-36 h-8 text-xs"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              data-testid="year-filter"
            />
          </div>
          <p className="text-xs text-slate-400">Formats: 2024, 2020-2025, 2024-</p>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400" data-testid="results-count">
              {total > 0 ? `${total.toLocaleString()} results found` : 'No results found'}
            </p>
            {total > 20 && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => searchPapers(Math.max(0, offset - 20))} data-testid="prev-page">Previous</Button>
                <Button variant="outline" size="sm" disabled={offset + 20 >= total} onClick={() => searchPapers(offset + 20)} data-testid="next-page">Next</Button>
              </div>
            )}
          </div>

          {papers.map((paper) => (
            <PaperCard
              key={paper.paperId}
              paper={paper}
              isExpanded={expandedPaper === paper.paperId}
              onExpand={() => loadPaperDetail(paper.paperId)}
              detail={expandedPaper === paper.paperId ? paperDetail : null}
              detailLoading={expandedPaper === paper.paperId && detailLoading}
              onBookmark={() => openBookmarkDialog(paper)}
              isBookmarked={bookmarkedIds.has(paper.paperId)}
              onCite={() => setCitePaper(paper)}
            />
          ))}

          {total > 20 && (
            <div className="flex justify-center gap-2 pt-4">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => searchPapers(Math.max(0, offset - 20))}>Previous</Button>
              <span className="text-sm text-slate-400 self-center">
                Page {Math.floor(offset / 20) + 1} of {Math.ceil(total / 20)}
              </span>
              <Button variant="outline" size="sm" disabled={offset + 20 >= total} onClick={() => searchPapers(offset + 20)}>Next</Button>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!results && !loading && (
        <div className="flex flex-col items-center justify-center h-64 text-slate-400" data-testid="search-empty-state">
          <BookOpen size={48} weight="thin" className="mb-3" />
          <p className="text-sm font-medium mb-1">Search scientific literature</p>
          <p className="text-xs text-center max-w-md">Find papers by topic, author, or keyword. Bookmark papers to your folders for easy reference.</p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {['CRISPR gene editing', 'synthetic biology', 'protein engineering', 'metabolic pathway'].map((term) => (
              <button
                key={term}
                className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-indigo-900/30 hover:text-indigo-300 rounded-full transition-colors"
                onClick={() => { setQuery(term); }}
                data-testid={`suggestion-${term.replace(/\s+/g, '-')}`}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bookmark to Folder Dialog */}
      <Dialog open={!!bookmarkPaper} onOpenChange={() => setBookmarkPaper(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkSimple size={18} className="text-indigo-400" /> Save to Folder
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300 truncate mb-3">{bookmarkPaper?.title}</p>

          {folders.length === 0 && !creatingFolder ? (
            <p className="text-sm text-slate-400 text-center py-4">No folders yet. Create one below.</p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="folder-list">
              {folders.map((f) => (
                <button
                  key={f.id}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${selectedFolder === f.id ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-500/30' : 'hover:bg-slate-800'}`}
                  onClick={() => setSelectedFolder(f.id)}
                  data-testid={`select-folder-${f.id}`}
                >
                  <FolderSimple size={16} weight={selectedFolder === f.id ? 'fill' : 'regular'} className={selectedFolder === f.id ? 'text-amber-500' : 'text-slate-400'} />
                  <span className="flex-1 truncate">{f.name}</span>
                  {selectedFolder === f.id && <Check size={14} className="text-indigo-400" />}
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2 mt-2">
            <Input
              placeholder="New folder name..."
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolderAndBookmark()}
              className="flex-1"
              data-testid="new-folder-for-bookmark"
            />
            <Button variant="outline" size="sm" onClick={handleCreateFolderAndBookmark} disabled={!newFolderName.trim() || creatingFolder}>
              <FolderSimplePlus size={14} />
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBookmarkPaper(null)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleBookmark} disabled={!selectedFolder} data-testid="confirm-bookmark">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Citation Dialog */}
      <CitationDialog paper={citePaper} onClose={() => setCitePaper(null)} />
    </div>
  );
}

const CitationDialog = ({ paper, onClose }) => {
  const [style, setStyle] = useState('apa');
  if (!paper) return null;

  const formatters = { apa: formatAPA, mla: formatMLA, chicago: formatChicago };
  const citation = formatters[style](paper);

  const handleCopy = () => {
    navigator.clipboard.writeText(citation);
    toast.success('Citation copied to clipboard');
  };

  return (
    <Dialog open={!!paper} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardText size={18} className="text-indigo-400" /> Cite Paper
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-300 truncate mb-3">{paper.title}</p>

        {/* Style tabs */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg" data-testid="citation-style-tabs">
          {CITATION_STYLES.map((s) => (
            <button
              key={s.id}
              className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-all ${style === s.id ? 'bg-slate-800 text-indigo-300 shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
              onClick={() => setStyle(s.id)}
              data-testid={`citation-style-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Formatted citation */}
        <div className="relative group">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-sm text-slate-200 leading-relaxed font-serif select-all" data-testid="citation-text">
            {citation}
          </div>
          <button
            onClick={handleCopy}
            className="absolute top-2 right-2 p-1.5 bg-slate-800 border border-slate-700 rounded-md shadow-sm opacity-0 group-hover:opacity-100 hover:bg-indigo-900/30 hover:border-indigo-500/30 transition-all"
            data-testid="copy-citation-btn"
          >
            <Copy size={14} className="text-slate-400" />
          </button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleCopy} data-testid="copy-citation-main-btn">
            <Copy size={14} className="mr-1.5" /> Copy Citation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PaperCard = ({ paper, isExpanded, onExpand, detail, detailLoading, onBookmark, isBookmarked, onCite }) => {
  const authors = (paper.authors || []).slice(0, 5).map(a => a.name).join(', ');
  const moreAuthors = (paper.authors || []).length > 5 ? ` +${paper.authors.length - 5} more` : '';

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-all" data-testid={`paper-${paper.paperId}`}>
      {/* Title + actions */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-slate-200 leading-snug mb-1">
            {paper.url ? (
              <a href={paper.url} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400 transition-colors">
                {paper.title}
              </a>
            ) : paper.title}
          </h3>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400 mb-2">
            {paper.year && (
              <span className="flex items-center gap-1"><CalendarBlank size={12} /> {paper.year}</span>
            )}
            {paper.venue && (
              <span className="bg-slate-100 px-2 py-0.5 rounded-full truncate max-w-[200px]">{paper.venue}</span>
            )}
            {paper.citationCount > 0 && (
              <span className="flex items-center gap-1"><Quotes size={12} /> {paper.citationCount.toLocaleString()} citations</span>
            )}
            {paper.openAccessPdf?.url && (
              <a href={paper.openAccessPdf.url} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                <ArrowSquareOut size={12} /> PDF
              </a>
            )}
          </div>

          {/* Authors */}
          {authors && (
            <p className="text-xs text-slate-400 flex items-center gap-1 mb-2">
              <UsersIcon size={12} /> {authors}{moreAuthors}
            </p>
          )}

          {/* Abstract */}
          {paper.abstract && (
            <p className="text-sm text-slate-300 leading-relaxed line-clamp-2">{paper.abstract}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1 flex-shrink-0">
          <Button
            variant="ghost" size="sm"
            className="text-slate-400 hover:text-indigo-400"
            onClick={onCite}
            title="Cite"
            data-testid={`cite-${paper.paperId}`}
          >
            <ClipboardText size={18} />
          </Button>
          <Button
            variant="ghost" size="sm"
            className={isBookmarked ? 'text-indigo-400' : 'text-slate-400 hover:text-indigo-400'}
            onClick={onBookmark}
            data-testid={`bookmark-${paper.paperId}`}
          >
            <BookmarkSimple size={18} weight={isBookmarked ? 'fill' : 'regular'} />
          </Button>
          <Button variant="ghost" size="sm" className="text-slate-400" onClick={onExpand} data-testid={`expand-${paper.paperId}`}>
            {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
          </Button>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-slate-700" data-testid="paper-detail">
          {detailLoading ? (
            <div className="flex items-center justify-center py-6 text-slate-400">
              <Spinner size={20} className="animate-spin mr-2" /> Loading details...
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Full abstract */}
              {detail.abstract && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase mb-1">Abstract</h4>
                  <p className="text-sm text-slate-300 leading-relaxed">{detail.abstract}</p>
                </div>
              )}

              {/* External IDs / DOI */}
              {detail.externalIds?.DOI && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase mb-1">DOI</h4>
                  <a href={`https://doi.org/${detail.externalIds.DOI}`} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-400 hover:underline">
                    {detail.externalIds.DOI}
                  </a>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-slate-200">{(detail.citationCount || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-400">Citations</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-slate-200">{(detail.referenceCount || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-400">References</p>
                </div>
              </div>

              {/* References preview */}
              {detail.references?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">Top References</h4>
                  <div className="space-y-1">
                    {detail.references.slice(0, 5).map((ref, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <ArrowRight size={10} className="text-slate-400 mt-1 flex-shrink-0" />
                        <span className="text-slate-300">
                          {ref.title || 'Untitled'} {ref.year ? `(${ref.year})` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Citing papers preview */}
              {detail.citations?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">Cited By</h4>
                  <div className="space-y-1">
                    {detail.citations.slice(0, 5).map((cit, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <ArrowRight size={10} className="text-indigo-400 mt-1 flex-shrink-0" />
                        <span className="text-slate-300">
                          {cit.title || 'Untitled'} {cit.year ? `(${cit.year})` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
